import { CandidateSearchFilters } from '__generated__/graphql'
import { CandidateFilterState } from './candidateFilterState'

const toDateValue = (s: string | undefined): string | undefined => {
    if (!s) return undefined
    const d = new Date(s)
    return isNaN(d.getTime()) ? undefined : d.toISOString()
}

const ids = <T>(a: T[]): T[] | undefined => (a.length > 0 ? a : undefined)

const dateRange = (gte: string | undefined, lte: string | undefined) => {
    const g = toDateValue(gte)
    const l = toDateValue(lte)
    if (g && l && g > l) return undefined
    return g || l ? { gte: g, lte: l } : undefined
}

const intRange = (gte: number | undefined, lte: number | undefined) => {
    if (gte && lte && gte > lte) return undefined
    return gte || lte ? { gte, lte } : undefined
}

const preference = (first: string[], second: string[], third: string[]) =>
    first.length > 0 || second.length > 0 || third.length > 0
        ? { first: ids(first), second: ids(second), third: ids(third) }
        : undefined

export const toGraphQLFilters = (
    state: CandidateFilterState
): CandidateSearchFilters => ({
    quickSearch: state.quickSearch,
    name: state.name,
    colorNames: ids(state.colorNames),
    recruitingStatuses: ids(state.recruitingStatus),
    outreachStatuses: ids(state.outreachStatus),
    systemStatuses: ids(state.systemStatus),
    starRatingGte: state.starRatingMin,
    starRatingLte: state.starRatingMax,
    screeningNoteTemplateIds: ids(state.screeningNoteTemplateIds),
    screeningNoteStarRatingGte: state.screeningNoteStarRatingMin,
    screeningNoteStarRatingLte: state.screeningNoteStarRatingMax,
    companyIds: ids(state.companyIds),
    companyTimeFrame: state.companyTimeFrame,
    companyDateRange: dateRange(state.companyRangeGte, state.companyRangeLte),
    companyAdvancedStart: dateRange(
        state.companyAdvancedStartGte,
        state.companyAdvancedStartLte
    ),
    companyAdvancedEnd: dateRange(
        state.companyAdvancedEndGte,
        state.companyAdvancedEndLte
    ),
    sectorNames: ids(state.sectorNames),
    sectorNamesNot: ids(state.sectorNamesNot),
    sectorTimeFrame: state.sectorTimeFrame,
    sectorDateRange: dateRange(state.sectorRangeGte, state.sectorRangeLte),
    sectorTopTierOnly: state.sectorTopTierOnly || undefined,
    industryNames: ids(state.industryNames),
    industryNamesNot: ids(state.industryNamesNot),
    industryTimeFrame: state.industryTimeFrame,
    industryDateRange: dateRange(
        state.industryRangeGte,
        state.industryRangeLte
    ),
    industryTopTierOnly: state.industryTopTierOnly || undefined,
    positionTitles: ids(state.positionTitles),
    positionTimeFrame: state.positionTimeFrame,
    positionDateRange: dateRange(
        state.positionRangeGte,
        state.positionRangeLte
    ),
    locationIds: ids(state.locationIds),
    locationTimeFrame: state.locationTimeFrame,
    locationDateRange: dateRange(
        state.locationRangeGte,
        state.locationRangeLte
    ),
    locationIncludeStateCountry: state.locationIncludeStateCountry || undefined,
    locationText: ids(state.locationText),
    locationTextTimeFrame: state.locationTextTimeFrame,
    locationTextDateRange: dateRange(
        state.locationTextRangeGte,
        state.locationTextRangeLte
    ),
    analystClassYearGte: state.analystClassYearGte,
    analystClassYearLte: state.analystClassYearLte,
    searchesFilter:
        state.searchFilters.length > 0
            ? { items: state.searchFilters }
            : undefined,
    genderIds: ids(state.genderIds),
    ethnicityIds: ids(state.ethnicityIds),
    hometownSearchTerms: ids(state.hometownSearchTerms),
    citizenshipIds: ids(state.citizenshipIds),
    visaStatusIds: ids(state.visaStatusIds),
    visaStatusIdsNot: ids(state.visaStatusIdsNot),
    qualitativeIds: ids(state.qualitativeIds),
    qualitativeIdsNot: ids(state.qualitativeIdsNot),
    revenueExperienceIds: ids(state.revenueExperienceIds),
    lgbtqIds: ids(state.lgbtqIds),
    cpiPlacedIds: ids(state.cpiPlacedIds),
    cpiPlacedIdsNot: ids(state.cpiPlacedIdsNot),
    referenceAttributeIds: ids(state.referenceAttributeIds),
    mbaTierIds: ids(state.mbaTierIds),
    industryPreference: preference(
        state.industryPrefFirst,
        state.industryPrefSecond,
        state.industryPrefThird
    ),
    sectorPreference: preference(
        state.sectorPrefFirst,
        state.sectorPrefSecond,
        state.sectorPrefThird
    ),
    companyPreference:
        state.companyPrefFirst.length > 0 || state.companyPrefSecond.length > 0
            ? {
                  first: ids(state.companyPrefFirst),
                  second: ids(state.companyPrefSecond),
              }
            : undefined,
    locationPreference: preference(
        state.locationPrefFirst,
        state.locationPrefSecond,
        state.locationPrefThird
    ),
    undergradCollegeIds: ids(state.undergradCollegeIds),
    mbaCollegeIds: ids(state.mbaCollegeIds),
    undergradCollegeNames: ids(state.undergradCollegeNames),
    graduateCollegeNames: ids(state.graduateCollegeNames),
    majorIds: ids(state.majorIds),
    degreeTypeIds: ids(state.degreeTypeIds),
    degreeTypeIdsNot: ids(state.degreeTypeIdsNot),
    gpa: (() => {
        const { gpaRangeMin: min, gpaRangeMax: max, gpaNoGpaMode: mode } = state

        if (mode === 'only') return { noGpaMode: 'only' as const }

        if (min === -1) return { noGpaMode: 'only' as const }

        const isDefaultRange =
            (min === undefined || min === 0) && (max === undefined || max === 4)
        const isDefaultMode = mode === undefined || mode === 'include'

        if (isDefaultRange && isDefaultMode) return undefined

        const lte = max === 4 || max === undefined ? 6 : max

        if (mode === 'exclude') {
            return { gte: min ?? 0, lte }
        }

        return { gte: min ?? 0, lte, noGpaMode: 'include' as const }
    })(),
    graduationDate:
        state.graduationRangeGte ||
        state.graduationRangeLte ||
        state.graduationEducationType
            ? {
                  yearRange: intRange(
                      state.graduationRangeGte,
                      state.graduationRangeLte
                  ),
                  educationType: state.graduationEducationType,
              }
            : undefined,
    startDateRange: dateRange(state.startDateGte, state.startDateLte),
    prefStartDates: ids(state.prefStartDates),
    processStatusGroup: state.processStatusGroup,
    processStatusFilter:
        state.processStatusIds.length > 0 ||
        state.processStatusGroupIds.length > 0
            ? {
                  statusIds: ids(state.processStatusIds),
                  groupIds: ids(state.processStatusGroupIds),
              }
            : undefined,
    remeetFilter: state.remeetFilter || undefined,
    includeDeactivated: state.includeDeactivated || undefined,
})
